import { GetterTree, MutationTree, ActionTree } from 'vuex';
import Reservation from '@/model/Reservation';
import Contact, { ContactMapping } from '@/model/Contact';
import httpClient from '@/api/http-client';
import { searchForReservations, SearchType } from '@/services/search';
import { toMapById } from '@/model/model-utils';
import IRootState, { IContactSearchState } from './store-state';

function contactSearchKind(t: SearchType): 'name' | 'phone' | 'email' | 'all' | null {
  switch (t) {
    case SearchType.ContactName:
      return 'name';
    case SearchType.ContactPhone:
      return 'phone';
    case SearchType.ContactEmail:
      return 'email';
    case SearchType.ContactAll:
      return 'all';
    default:
      return null;
  }
}

export class ContactSearchState implements IContactSearchState {
  type: SearchType = SearchType.None;

  query: string = '';

  requestCount: number = 0;

  localContacts: Contact[] = [];

  apiContacts: Contact[] = [];
}

function contactsFromReservations(reservations: Reservation[]): Contact[] {
  const importances = new Map<number, number>();
  const contacts: Contact[] = [];
  reservations.forEach((r) => {
    if (!r.contact) return;
    if (!importances.has(r.contact.id)) {
      contacts.push(r.contact);
      importances.set(r.contact.id, r.contact.reservationCount ?? 1);
    }
  });
  return contacts.sort((o1, o2) => {
    const res = (importances.get(o2.id) ?? 0) - (importances.get(o1.id) ?? 0);
    if (res !== 0) return res;
    return o1.id - o2.id;
  });
}

const mutations = <MutationTree<IContactSearchState>>{
  RESET(state: IContactSearchState) {
    Object.assign(state, new ContactSearchState());
  },
  RESET_SEARCH(state: IContactSearchState) {
    Object.assign(state, new ContactSearchState());
  },
  SET_SEARCH_QUERY(state: IContactSearchState, p: { query: string, type: SearchType }) {
    state.query = p.query;
    state.type = p.type;
    if (p.query.length < 3) { state.localContacts = []; state.apiContacts = []; }
  },
  CHANGE_SEARCH_REQUEST_COUNT(state: IContactSearchState, increment: boolean) {
    state.requestCount = increment ? state.requestCount + 1 : state.requestCount - 1;
  },
  SET_SEARCH_RESULTS(
    state: IContactSearchState,
    p: { query: string, localContacts?: Contact[], apiContacts?: Contact[] },
  ) {
    if (p.localContacts) state.localContacts = p.localContacts;
    if (p.apiContacts) state.apiContacts = p.apiContacts;
  },
};

const actions = <ActionTree<IContactSearchState, IRootState>>{
  async search({
    state, commit, rootGetters, rootState,
  }, p: { query: string, type: SearchType }) {
    if (state.query === p.query && state.type === p.type) return;

    console.log('CONTACT SEARCH query:', p.query);
    commit('SET_SEARCH_QUERY', p);
    commit('CHANGE_SEARCH_REQUEST_COUNT', true);

    setTimeout(async () => {
      try {
        if (state.query !== p.query || p.query.length < 3 || state.type !== p.type) return;

        const { reservationsById } = rootState.reservations;
        const localReservations = searchForReservations(state.query, state.type, reservationsById);
        const localContacts = contactsFromReservations(localReservations);
        commit('SET_SEARCH_RESULTS', { localContacts });
        console.log('CONTACT SEARCH LOCAL results:', localContacts);

        const kind = contactSearchKind(p.type);
        if (!kind) {
          return;
        }

        console.log('CONTACT SEARCH API start:', p.query);
        const apiDtos = await httpClient.contactSearch(p.query, kind);
        const apiContacts = apiDtos.map(
          (o) => new Contact(o, rootGetters as ContactMapping),
        );
        console.log('CONTACT SEARCH API end:', p.query);

        if (state.query !== p.query || state.type !== p.type) return;

        commit('SET_SEARCH_RESULTS', { apiContacts });
        console.log('CONTACT SEARCH API results:', apiContacts);
      } catch (e) {
        console.log('CONTACT SEARCH API error:', e);
      } finally {
        commit('CHANGE_SEARCH_REQUEST_COUNT', false);
      }
    }, 500);
  },
  resetSearch({ commit }) {
    console.log('reset contact search');
    commit('RESET_SEARCH');
  },
};

const getters = <GetterTree<IContactSearchState, IRootState>>{
  searchContacts(state, localGetters, rootState, rootGetters): Contact[] {
    const apiById = toMapById(state.apiContacts);
    const contacts = [...state.apiContacts, ...state.localContacts.filter((c) => !apiById.get(c.id))];
    return contacts;
  },
  isSearching(state): boolean {
    return state.requestCount !== 0 && state.query.length >= 3;
  },
};

export const NameStore = {
  namespaced: true,
  state: new ContactSearchState(),
  mutations,
  actions,
  getters,
};

export const PhoneStore = {
  namespaced: true,
  state: new ContactSearchState(),
  mutations,
  actions,
  getters,
};

export const EmailStore = {
  namespaced: true,
  state: new ContactSearchState(),
  mutations,
  actions,
  getters,
};
